import React, { useState, useEffect, useMemo } from 'react';
import {
  BellRing,
  Calendar,
  Clock,
  Search,
  Phone,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  Users,
  Building2,
  ChevronRight,
  Sparkles,
  RefreshCw,
  Zap,
  ShieldCheck,
  CalendarDays
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import reminderService from '../../services/reminderService';
import SetReminderModal from './SetReminderModal';
import SetCollectionDateModal from './SetCollectionDateModal';

const WHATSAPP_SVG_ICON = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.472 14.382C17.112 14.202 15.348 13.334 15.018 13.214C14.688 13.094 14.448 13.034 14.208 13.394C13.968 13.754 13.284 14.558 13.074 14.798C12.864 15.038 12.654 15.068 12.294 14.888C11.934 14.708 10.774 14.328 9.398 13.102C8.326 12.146 7.602 10.966 7.392 10.606C7.182 10.246 7.37 10.052 7.55 9.873C7.713 9.711 7.912 9.447 8.092 9.237C8.272 9.027 8.332 8.877 8.452 8.637C8.572 8.397 8.512 8.187 8.422 8.007C8.332 7.827 7.612 6.057 7.312 5.337C7.02 4.636 6.724 4.731 6.502 4.722C6.292 4.713 6.052 4.711 5.812 4.711C5.572 4.711 5.182 4.801 4.852 5.161C4.522 5.521 3.592 6.391 3.592 8.161C3.592 9.931 4.882 11.641 5.062 11.881C5.242 12.121 7.602 15.761 11.222 17.321C12.083 17.693 12.755 17.916 13.279 18.082C14.143 18.357 14.929 18.317 15.551 18.224C16.244 18.12 17.684 17.352 17.984 16.512C18.284 15.672 18.284 14.952 18.194 14.798C18.104 14.644 17.832 14.562 17.472 14.382Z"
      fill="#FFFFFF"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.004 2C6.48 2 2 6.48 2 12.004C2 13.774 2.463 15.438 3.275 16.892L2.052 21.365C1.986 21.606 2.055 21.865 2.233 22.043C2.378 22.188 2.576 22.268 2.78 22.268C2.868 22.268 2.957 22.253 3.043 22.222L7.693 20.615C9.049 21.503 10.472 22.008 12.004 22.008C17.528 22.008 22.008 17.528 22.008 12.004C22.008 6.48 17.528 2 12.004 2ZM4.004 12.004C4.004 7.592 7.592 4.004 12.004 4.004C16.416 4.004 20.004 7.592 20.004 12.004C20.004 16.416 16.416 20.004 12.004 20.004C10.59 20.004 9.255 19.539 8.163 18.665C7.994 18.53 7.781 18.47 7.568 18.502L4.35 19.615L5.201 16.502C5.285 16.196 5.213 15.867 5.009 15.626C4.364 14.862 4.004 13.472 4.004 12.004Z"
      fill="#FFFFFF"
    />
  </svg>
);

const RemindersHome = ({ userProfile }) => {
  const { t, lang } = useLang();

  const [loading, setLoading] = useState(true);
  const [remindersData, setRemindersData] = useState({
    summary: {
      total_outstanding: 0,
      total_overdue: 0,
      total_due_today: 0,
      total_customers: 0,
      customers_count: 0,
      suppliers_count: 0,
      overdue_count: 0,
      due_today_count: 0,
      upcoming_count: 0,
    },
    data: [],
  });

  // Filters
  const [partyTypeFilter, setPartyTypeFilter] = useState('all'); // all, customer, supplier
  const [statusTab, setStatusTab] = useState('all'); // overdue, due_today, upcoming, all
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [singleCustomerModal, setSingleCustomerModal] = useState(null);
  const [isCollectionDateModalOpen, setIsCollectionDateModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const businessName =
    userProfile?.business_name ||
    userProfile?.owner_name ||
    localStorage.getItem('business_name') ||
    'Moxpal Khata';

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const res = await reminderService.getReminderList();
      if (res && res.success) {
        setRemindersData({
          summary: res.summary || {},
          data: res.data || [],
        });
      }
    } catch (err) {
      console.error('Failed to load reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  const formatCurrency = (val) => {
    return `₹${parseFloat(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr || dateStr === '0000-00-00' || dateStr === 'null') return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const d = new Date(year, month, day);
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatSentDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '09:00 AM';
    try {
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        let hour = parseInt(parts[0], 10);
        const minute = parts[1];
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12;
        hour = hour ? hour : 12;
        const formattedHour = hour < 10 ? `0${hour}` : hour;
        return `${formattedHour}:${minute} ${ampm}`;
      }
      return timeStr;
    } catch (e) {
      return timeStr;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Filtered List based on Party Type, Status Tab, and Search Query
  const filteredItems = useMemo(() => {
    let list = remindersData.data || [];

    // 1. Party Type filter (all, customer, supplier)
    if (partyTypeFilter === 'customer') {
      list = list.filter((item) => item.party_type !== 'supplier');
    } else if (partyTypeFilter === 'supplier') {
      list = list.filter((item) => item.party_type === 'supplier');
    }

    // 2. Status Tab (overdue, due_today, upcoming, all)
    if (statusTab === 'overdue') {
      list = list.filter((item) => item.status === 'overdue');
    } else if (statusTab === 'due_today') {
      list = list.filter((item) => item.status === 'due_today');
    } else if (statusTab === 'upcoming') {
      list = list.filter((item) => item.status === 'upcoming');
    }

    // 3. Search Query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (item) =>
          item.customer_name?.toLowerCase().includes(q) ||
          item.mobile?.includes(q)
      );
    }

    return list;
  }, [remindersData.data, partyTypeFilter, statusTab, searchQuery]);

  // WhatsApp reminder action
  const handleSendWhatsApp = async (customer) => {
    const rawMobile = customer.mobile ? customer.mobile.replace(/\D/g, '') : '';
    if (!rawMobile) {
      alert('ग्राहक का मोबाइल नंबर उपलब्ध नहीं है।');
      return;
    }

    const formattedMobile = rawMobile.length === 10 ? `91${rawMobile}` : rawMobile;
    const amountStr = parseFloat(customer.pending_balance || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
    });
    const dueDateStr = customer.reminder_date ? formatDateDisplay(customer.reminder_date) : 'यथाशीघ्र';
    const linkStr = customer.share_link || 'https://khata.moxpal.com';

    let msg = '';
    const currentLang = (lang || localStorage.getItem('app_language') || 'hi').toLowerCase();

    if (currentLang.startsWith('en')) {
      msg =
        `Dear *${customer.customer_name}* 🙏\n\n` +
        `Your total pending balance with *${businessName}* is ₹*${amountStr}*.\n` +
        `Due date: *${dueDateStr}*. Please clear the payment on time.\n\n` +
        `📄 Click here to view account ledger & pay online:\n${linkStr}\n\n` +
        `Thank you!💐`;
    } else if (currentLang.startsWith('gu')) {
      msg =
        `નમસ્તે *${customer.customer_name}* જી 🙏\n\n` +
        `*${businessName}* તરફથી તમારા ખાતાની કુલ બાકી રકમ ₹*${amountStr}* છે.\n` +
        `કૃપા કરીને નિયત તારીખ *${dueDateStr}* સુધીમાં ચુકવણી કરવા વિનંતી.\n\n` +
        `📄 ખાતાવહી અને ઓનલાઇન રસીદ જોવા માટે અહીં ક્લિક કરો:\n${linkStr}\n\n` +
        `આભાર!💐`;
    } else if (currentLang.startsWith('mr')) {
      msg =
        `नमस्ते *${customer.customer_name}* जी 🙏\n\n` +
        `*${businessName}* कडून आपल्या खात्याची एकूण बाकी रक्कम ₹*${amountStr}* आहे.\n` +
        `कृपया देय तारीख *${dueDateStr}* पर्यंत पेमेंट करावे.\n\n` +
        `📄 खाते तपशील आणि ऑनलाइन पावती पाहण्यासाठी येथे क्लिक करा:\n${linkStr}\n\n` +
        `धन्यवाद!💐`;
    } else if (currentLang.startsWith('pa')) {
      msg =
        `ਨਮਸਤੇ *${customer.customer_name}* ਜੀ 🙏\n\n` +
        `*${businessName}* ਵੱਲੋਂ ਤੁਹਾਡੇ ਖਾਤੇ ਦਾ ਕੁੱਲ ਬਕਾਇਆ ₹*${amountStr}* ਹੈ।\n` +
        `ਕਿਰਪਾ ਕਰਕੇ ਨਿਯਤ ਮਿਤੀ *${dueDateStr}* ਤੱਕ ਭੁਗਤਾਨ ਕਰਨ ਦੀ ਖੇਚਲ ਕਰੋ।\n\n` +
        `📄 ਖਾਤਾ ਵੇਰਵਾ ਅਤੇ ਆਨਲਾਈਨ ਰਸੀਦ ਵੇਖਣ ਲਈ ਇੱਥੇ ਕਲਿੱਕ ਕਰੋ:\n${linkStr}\n\n` +
        `ਧੰਨਵਾਦ!💐`;
    } else if (currentLang.startsWith('ta')) {
      msg =
        `வணக்கம் *${customer.customer_name}* அவர்களே 🙏\n\n` +
        `*${businessName}* நிறுவனத்தில் உங்கள் மொத்த நிலுவைத் தொகை ₹*${amountStr}* ஆகும்.\n` +
        `கெடு தேதி: *${dueDateStr}*. தயவுசெய்து உரிய நேரத்தில் பணம் செலுத்தவும்.\n\n` +
        `📄 கணக்கு விவரங்கள் மற்றும் ஆன்லைன் ரசீதைப் பார்க்க இங்கே கிளிக் செய்யவும்:\n${linkStr}\n\n` +
        `நன்றி!💐`;
    } else if (currentLang.startsWith('te')) {
      msg =
        `నమస్తే *${customer.customer_name}* గారు 🙏\n\n` +
        `*${businessName}* నుండి మీ ఖాతా బకాయి మొత్తం ₹*${amountStr}*.\n` +
        `గడువు తేదీ: *${dueDateStr}*. దయచేసి సకాలంలో చెల్లించండి.\n\n` +
        `📄 ఖాతా వివరాలు మరియు ఆన్‌లైన్ రసీదును చూడటానికి ఇక్కడ క్లిక్ చేయండి:\n${linkStr}\n\n` +
        `ధన్యవాదాలు!💐`;
    } else {
      // Default: Hindi / Hinglish
      msg =
        `नमस्ते *${customer.customer_name}* जी 🙏\n\n` +
        `*${businessName}* की ओर से आपके खाते का कुल बकाया ₹*${amountStr}* है।\n` +
        `कृपया नियत तिथि *${dueDateStr}* तक भुगतान करने का कष्ट करें।\n\n` +
        `📄 खाता विवरण व ऑनलाइन रसीद देखने के लिए यहाँ क्लिक करें:\n${linkStr}\n\n` +
        `धन्यवाद!💐`;
    }

    const waUrl = `https://wa.me/${formattedMobile}?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank');

    try {
      await reminderService.markReminderSent(customer.customer_id);
      const toastText = (t(LangKeys.reminders_wa_sent_toast) || '{name} को WhatsApp रिमाइंडर भेजा गया!').replace('{name}', customer.customer_name);
      showToast(toastText);
      setRemindersData((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item.customer_id === customer.customer_id
            ? { ...item, last_reminder_sent_at: new Date().toISOString() }
            : item
        ),
      }));
    } catch (e) {
      console.error(e);
    }
  };

  // SMS action
  const handleSendSMS = (customer) => {
    if (!customer.mobile) return;
    const amountStr = parseFloat(customer.pending_balance || 0).toLocaleString('en-IN');
    const dueDateStr = customer.reminder_date ? formatDateDisplay(customer.reminder_date) : '';
    const linkStr = customer.share_link || 'https://khata.moxpal.com';
    const currentLang = (lang || localStorage.getItem('app_language') || 'hi').toLowerCase();

    let bodyText = `Dear ${customer.customer_name}, your balance of Rs ${amountStr} is due with ${businessName}. View ledger & pay: ${linkStr}`;
    if (!currentLang.startsWith('en')) {
      bodyText = `नमस्ते ${customer.customer_name}, ${businessName} पर आपका कुल बकाया रु ${amountStr} है। खाता देखने व ऑनलाइन पे करने के लिए क्लिक करें: ${linkStr}`;
    }
    const body = encodeURIComponent(bodyText);
    window.open(`sms:${customer.mobile}?body=${body}`, '_self');
  };

  // Call action
  const handleCall = (mobile) => {
    if (!mobile) return;
    window.open(`tel:${mobile}`, '_self');
  };

  // Save single reminder date
  const handleSaveReminderDate = async (payload) => {
    const res = await reminderService.setReminderDate(payload);
    if (res && res.success) {
      showToast(t(LangKeys.reminders_date_updated) || 'रिमाइंडर तारीख सुरक्षित कर दी गई!');
      fetchReminders();
    }
  };

  // Bulk save reminder date
  const handleBulkSaveDate = async (payload) => {
    const res = await reminderService.bulkSetReminderDate(payload);
    if (res && res.success) {
      showToast(t(LangKeys.reminders_date_updated) || 'रिमाइंडर तारीख सुरक्षित कर दी गई!');
      fetchReminders();
    }
  };

  const summary = remindersData.summary || {};

  return (
    <div style={styles.container}>
      {/* Toast Notification */}
      {toastMessage && (
        <div style={styles.toast}>
          <CheckCircle2 size={18} color="#10B981" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 1. TOP BLUE GRADIENT HEADER & HERO (Matching Flutter Screenshots 1-4) */}
      <div style={styles.blueHeroSection}>
        {/* App Bar */}
        <div style={styles.appBar}>
          <div style={styles.appBarLeft}>
            <span style={styles.appBarTitle}>
              {t(LangKeys.reminders_title) || 'Payment Reminder'}
            </span>
          </div>
          <button style={styles.refreshIconBtn} onClick={fetchReminders} title="Refresh">
            <RefreshCw size={18} color="#FFFFFF" />
          </button>
        </div>

        {/* Hero Card: "Collect Money 3x Faster" */}
        <div style={styles.heroCard}>
          <div style={styles.heroCardLeft}>
            <div style={styles.lightningIconBox}>
              <Zap size={20} color="#2563EB" fill="#2563EB" />
            </div>
            <div>
              <div style={styles.heroCardTitle}>
                {t(LangKeys.reminders_collect_faster) || 'Collect Money 3x Faster'}
              </div>
              <div style={styles.heroCardSub}>
                {summary.total_customers || 0} {t(LangKeys.reminders_customers) || 'Customers'} • {formatCurrency(summary.total_outstanding)}
              </div>
            </div>
          </div>
          <button
            style={styles.heroSetDueBtn}
            onClick={() => setIsCollectionDateModalOpen(true)}
          >
            <span>{t(LangKeys.reminders_set_due_date) || 'Set Due Date'}</span>
            <span>›</span>
          </button>
        </div>

        {/* 3 Stat Cards Row */}
        <div style={styles.statsRow}>
          {/* Total Dues */}
          <div style={styles.statCardGlass}>
            <div style={styles.statGlassHeader}>
              <span style={{ fontSize: '13px', marginRight: '4px' }}>📋</span>
              <span>{t(LangKeys.reminders_total_dues) || 'Total Dues'}</span>
            </div>
            <div style={styles.statGlassVal}>{formatCurrency(summary.total_outstanding)}</div>
            <div style={styles.statGlassSub}>
              {summary.total_customers || 0} {t(LangKeys.reminders_customers) || 'Customers'}
            </div>
          </div>

          {/* Overdue */}
          <div
            style={{
              ...styles.statCardGlass,
              backgroundColor: 'rgba(124, 58, 237, 0.22)',
              cursor: 'pointer',
            }}
            onClick={() => setStatusTab('overdue')}
          >
            <div style={styles.statGlassHeader}>
              <span style={{ fontSize: '13px', marginRight: '4px' }}>⚠️</span>
              <span>{t(LangKeys.reminders_overdue) || 'Overdue'}</span>
            </div>
            <div style={styles.statGlassVal}>{formatCurrency(summary.total_overdue)}</div>
            <div style={styles.statGlassSub}>
              {summary.overdue_count || 0} {t(LangKeys.reminders_customers) || 'Customers'}
            </div>
          </div>

          {/* Due Today */}
          <div
            style={{
              ...styles.statCardGlass,
              backgroundColor: 'rgba(14, 165, 233, 0.22)',
              cursor: 'pointer',
            }}
            onClick={() => setStatusTab('due_today')}
          >
            <div style={styles.statGlassHeader}>
              <span style={{ fontSize: '13px', marginRight: '4px' }}>📅</span>
              <span>{t(LangKeys.reminders_due_today) || 'Due Today'}</span>
            </div>
            <div style={styles.statGlassVal}>{formatCurrency(summary.total_due_today)}</div>
            <div style={styles.statGlassSub}>
              {summary.due_today_count || 0} {t(LangKeys.reminders_customers) || 'Customers'}
            </div>
          </div>
        </div>
      </div>

      {/* 2. WHITE CONTENT AREA */}
      <div style={styles.contentArea}>
        {/* Search Bar */}
        <div style={styles.searchBar}>
          <Search size={18} color="#94A3B8" />
          <input
            type="text"
            placeholder={t(LangKeys.reminders_search_placeholder) || 'Search by customer name or mobile...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button style={styles.clearSearchBtn} onClick={() => setSearchQuery('')}>
              ✕
            </button>
          )}
        </div>

        {/* Party Type Filter Pills */}
        <div style={styles.pillRow}>
          <button
            style={{
              ...styles.partyPill,
              ...(partyTypeFilter === 'all' ? styles.partyPillActive : {}),
            }}
            onClick={() => setPartyTypeFilter('all')}
          >
            {t(LangKeys.reminders_all) || 'All'} ({summary.total_customers || remindersData.data.length})
          </button>

          <button
            style={{
              ...styles.partyPill,
              ...(partyTypeFilter === 'customer' ? styles.partyPillActive : {}),
            }}
            onClick={() => setPartyTypeFilter('customer')}
          >
            <span style={{ marginRight: '4px' }}>👤</span>
            {t(LangKeys.reminders_customers) || 'Customers'} ({summary.customers_count || remindersData.data.filter((i) => i.party_type !== 'supplier').length})
          </button>

          <button
            style={{
              ...styles.partyPill,
              ...(partyTypeFilter === 'supplier' ? styles.partyPillActive : {}),
            }}
            onClick={() => setPartyTypeFilter('supplier')}
          >
            <span style={{ marginRight: '4px' }}>🏬</span>
            {t(LangKeys.reminders_suppliers) || 'Suppliers'} ({summary.suppliers_count || remindersData.data.filter((i) => i.party_type === 'supplier').length})
          </button>
        </div>

        {/* Status Underlined Tabs (Matching Flutter) */}
        <div style={styles.statusTabBar}>
          <button
            style={{
              ...styles.statusTabBtn,
              ...(statusTab === 'overdue' ? styles.statusTabBtnActive : {}),
            }}
            onClick={() => setStatusTab('overdue')}
          >
            {t(LangKeys.reminders_overdue) || 'Overdue'} ({summary.overdue_count || 0})
            {statusTab === 'overdue' && <span style={styles.activeTabLine} />}
          </button>

          <button
            style={{
              ...styles.statusTabBtn,
              ...(statusTab === 'due_today' ? styles.statusTabBtnActive : {}),
            }}
            onClick={() => setStatusTab('due_today')}
          >
            {t(LangKeys.reminders_due_today) || 'Due Today'} ({summary.due_today_count || 0})
            {statusTab === 'due_today' && <span style={styles.activeTabLine} />}
          </button>

          <button
            style={{
              ...styles.statusTabBtn,
              ...(statusTab === 'upcoming' ? styles.statusTabBtnActive : {}),
            }}
            onClick={() => setStatusTab('upcoming')}
          >
            {t(LangKeys.reminders_upcoming) || 'Upcoming'} ({summary.upcoming_count || 0})
            {statusTab === 'upcoming' && <span style={styles.activeTabLine} />}
          </button>

          <button
            style={{
              ...styles.statusTabBtn,
              ...(statusTab === 'all' ? styles.statusTabBtnActive : {}),
            }}
            onClick={() => setStatusTab('all')}
          >
            {t(LangKeys.reminders_all_customers) || 'All Customers'} ({summary.total_customers || remindersData.data.length})
            {statusTab === 'all' && <span style={styles.activeTabLine} />}
          </button>
        </div>

        {/* 3. TAB CONTENT / LIST / EMPTY STATES */}
        {loading ? (
          <div style={styles.loadingBox}>
            <div className="premium-spinner"></div>
            <p style={styles.loadingText}>Loading reminders...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          /* EMPTY STATE CARDS MATCHING SCREENSHOTS 1, 2, 3 */
          statusTab === 'overdue' ? (
            /* Screenshot 1: No Overdue */
            <div style={styles.emptyCardContainer}>
              <div style={styles.emptyCardInner}>
                <div style={styles.greenShieldOuterCircle}>
                  <div style={styles.greenShieldInnerCircle}>
                    <ShieldCheck size={28} color="#FFFFFF" strokeWidth={2.5} />
                  </div>
                </div>

                <div style={styles.greenStatusBadge}>
                  <span style={{ fontSize: '10px', marginRight: '4px' }}>●</span>
                  <span>{t(LangKeys.reminders_all_up_to_date) || 'All Up to Date'}</span>
                </div>

                <h3 style={styles.emptyCardHeading}>
                  {t(LangKeys.reminders_no_overdue_heading) || 'No overdue customers found'}
                </h3>
                <p style={styles.emptyCardSub}>
                  {t(LangKeys.reminders_no_overdue_sub) || 'Great! All customer dues are currently up to date with no overdue payments.'}
                </p>

                <button
                  style={styles.greenViewBtn}
                  onClick={() => setStatusTab('all')}
                >
                  <span>{t(LangKeys.reminders_view_all_customers) || 'View All Customers'}</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          ) : statusTab === 'due_today' ? (
            /* Screenshot 2: No Due Today */
            <div style={styles.emptyCardContainer}>
              <div style={styles.emptyCardInner}>
                <div style={styles.orangeCalendarOuterCircle}>
                  <div style={styles.orangeCalendarInnerCircle}>
                    <CalendarClock size={28} color="#FFFFFF" strokeWidth={2.5} />
                  </div>
                </div>

                <div style={styles.orangeStatusBadge}>
                  <span style={{ fontSize: '10px', marginRight: '4px' }}>●</span>
                  <span>{t(LangKeys.reminders_all_up_to_date) || 'All Up to Date'}</span>
                </div>

                <h3 style={styles.emptyCardHeading}>
                  {t(LangKeys.reminders_no_due_today_heading) || 'No customers due today'}
                </h3>
                <p style={styles.emptyCardSub}>
                  {t(LangKeys.reminders_no_due_today_sub) || 'No customer payment reminders are scheduled for today.'}
                </p>

                <button
                  style={styles.orangeViewBtn}
                  onClick={() => setStatusTab('all')}
                >
                  <span>{t(LangKeys.reminders_view_all_customers) || 'View All Customers'}</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          ) : statusTab === 'upcoming' ? (
            /* Screenshot 3: No Upcoming */
            <div style={styles.emptyCardContainer}>
              <div style={styles.emptyCardInner}>
                <div style={styles.blueCalendarOuterCircle}>
                  <div style={styles.blueCalendarInnerCircle}>
                    <CalendarDays size={28} color="#FFFFFF" strokeWidth={2.5} />
                  </div>
                </div>

                <div style={styles.blueStatusBadge}>
                  <span style={{ fontSize: '10px', marginRight: '4px' }}>●</span>
                  <span>{t(LangKeys.reminders_set_due_date) || 'Set Due Date'}</span>
                </div>

                <h3 style={styles.emptyCardHeading}>
                  {t(LangKeys.reminders_no_upcoming_heading) || 'No upcoming reminders'}
                </h3>
                <p style={styles.emptyCardSub}>
                  {t(LangKeys.reminders_no_upcoming_sub) || 'Set collection dates for customers to send timely automated reminders.'}
                </p>

                <button
                  style={styles.blueViewBtn}
                  onClick={() => setIsCollectionDateModalOpen(true)}
                >
                  <span>{t(LangKeys.reminders_set_due_date) || 'Set Due Date'}</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          ) : (
            /* Generic Empty */
            <div style={styles.emptyCardContainer}>
              <div style={styles.emptyCardInner}>
                <div style={styles.blueCalendarOuterCircle}>
                  <BellRing size={28} color="#2563EB" />
                </div>
                <h3 style={styles.emptyCardHeading}>
                  {t(LangKeys.reminders_no_customers_found) || 'No customers found'}
                </h3>
                <p style={styles.emptyCardSub}>
                  {t(LangKeys.reminders_no_customers_matched) || 'No customers matched your search query.'}
                </p>
              </div>
            </div>
          )
        ) : (
          /* CUSTOMER CARDS LIST (Matching Screenshot 4) */
          <div style={styles.cardsGrid}>
            {filteredItems.map((c) => {
              const initial = c.customer_name ? c.customer_name[0].toUpperCase() : 'C';

              return (
                <div key={c.customer_id} style={styles.custCard}>
                  {/* Top Row */}
                  <div style={styles.cardHeaderRow}>
                    <div style={styles.custAvatar}>{initial}</div>

                    <div style={styles.custHeaderMeta}>
                      <div style={styles.nameAndType}>
                        <span style={styles.custNameText}>{c.customer_name}</span>
                        <span style={styles.typeBadge}>
                          {c.party_type === 'supplier'
                            ? (t(LangKeys.reminders_suppliers) || 'Supplier')
                            : (t(LangKeys.reminders_customers) || 'Customer')}
                        </span>
                      </div>
                      <div style={styles.custMobileText}>
                        <span style={{ marginRight: '4px' }}>📱</span>
                        <span>{c.mobile || 'No Mobile'}</span>
                      </div>

                      {/* Status Badges Row (Due Today, Overdue, Due in X days) */}
                      {c.reminder_date && c.reminder_date !== '0000-00-00' && (
                        <div style={styles.badgeLineWrapper}>
                          {c.status === 'due_today' && (
                            <span style={styles.dueTodayYellowBadge}>
                              {t(LangKeys.reminders_due_today) || 'Due Today'}
                            </span>
                          )}
                          {c.status === 'overdue' && (
                            <span style={styles.overdueRedBadge}>
                              {t(LangKeys.reminders_overdue_by) || 'Overdue by'} {Math.abs(c.days_diff || 1)} {Math.abs(c.days_diff || 1) === 1 ? (t(LangKeys.reminders_day) || 'day') : (t(LangKeys.reminders_days) || 'days')}
                            </span>
                          )}
                          {c.status === 'upcoming' && (
                            <span style={styles.upcomingBlueBadge}>
                              {t(LangKeys.reminders_due_in) || 'Due in'} {c.days_diff || 1} {c.days_diff === 1 ? (t(LangKeys.reminders_day) || 'day') : (t(LangKeys.reminders_days) || 'days')}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Auto Reminder Active Pill with exact scheduled time */}
                      {(c.auto_reminder === 1 || c.auto_reminder === true || c.auto_reminder === '1') && (
                        <div style={styles.autoReminderPillWrapper}>
                          <span style={styles.autoReminderPurplePill}>
                            <Zap size={11} color="#7C3AED" fill="#7C3AED" />
                            <span>
                              {t(LangKeys.reminders_auto_reminder_active) || 'Auto Reminder Active'} • {formatTimeDisplay(c.reminder_time)}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>

                    <div style={styles.custBalanceBox}>
                      <div style={styles.custBalanceVal}>{formatCurrency(c.pending_balance)}</div>
                      <div style={styles.custBalanceLbl}>
                        {t(LangKeys.reminders_pending_balance) || 'Pending Balance'}
                      </div>
                      {c.reminder_date && c.reminder_date !== '0000-00-00' && (
                        <div style={styles.custDueDateLbl}>
                          {formatDateDisplay(c.reminder_date)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sent Badge Row (If last sent) */}
                  {c.last_reminder_sent_at && (
                    <div style={styles.sentBadgeWrapper}>
                      <span style={styles.sentGreenPill}>
                        ✓ {t(LangKeys.reminders_sent) || 'Sent'}: {formatSentDateDisplay(c.last_reminder_sent_at)}
                      </span>
                    </div>
                  )}

                  {/* 4 Action Buttons Bar (Matching Flutter Screenshot 4) */}
                  <div style={styles.actionsBar}>
                    {/* WhatsApp */}
                    <button
                      style={styles.actionBtnWhatsApp}
                      onClick={() => handleSendWhatsApp(c)}
                    >
                      {WHATSAPP_SVG_ICON}
                      <span>WhatsApp</span>
                    </button>

                    {/* SMS */}
                    <button
                      style={styles.actionBtnSMS}
                      onClick={() => handleSendSMS(c)}
                    >
                      <MessageSquare size={14} color="#2563EB" />
                      <span>SMS</span>
                    </button>

                    {/* Call */}
                    <button
                      style={styles.actionBtnCall}
                      onClick={() => handleCall(c.mobile)}
                    >
                      <Phone size={14} color="#0D9488" />
                      <span>{t(LangKeys.reminders_call) || 'Call'}</span>
                    </button>

                    {/* Date */}
                    <button
                      style={styles.actionBtnDate}
                      onClick={() => setSingleCustomerModal(c)}
                    >
                      <Calendar size={14} color="#7C3AED" />
                      <span>
                        {c.reminder_date
                          ? formatDateDisplay(c.reminder_date)
                          : (t(LangKeys.reminders_date) || 'Date')}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL 1: SET COLLECTION DATE (BULK / 3X FASTER) */}
      {isCollectionDateModalOpen && (
        <SetCollectionDateModal
          isOpen={isCollectionDateModalOpen}
          onClose={() => setIsCollectionDateModalOpen(false)}
          customers={remindersData.data || []}
          totalOutstanding={summary.total_outstanding || 0}
          onSaveReminderDate={handleSaveReminderDate}
          onBulkSetDate={handleBulkSaveDate}
        />
      )}

      {/* MODAL 2: SINGLE CUSTOMER DATE PICKER */}
      {singleCustomerModal && (
        <SetReminderModal
          isOpen={!!singleCustomerModal}
          onClose={() => setSingleCustomerModal(null)}
          customer={singleCustomerModal}
          onSave={async (payload) => {
            await handleSaveReminderDate(payload);
            setSingleCustomerModal(null);
          }}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  toast: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    backgroundColor: '#0F172A',
    color: '#FFFFFF',
    padding: '12px 20px',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13.5px',
    fontWeight: '600',
    zIndex: 99999,
  },
  blueHeroSection: {
    background: 'linear-gradient(145deg, #1E40AF 0%, #2563EB 50%, #3B82F6 100%)',
    borderRadius: '24px',
    padding: '20px 24px 24px 24px',
    color: '#FFFFFF',
    boxShadow: '0 10px 30px rgba(37, 99, 235, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  appBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  appBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  appBarTitle: {
    fontSize: '19px',
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: '-0.01em',
  },
  refreshIconBtn: {
    background: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  heroCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '18px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    flexWrap: 'wrap',
    gap: '12px',
  },
  heroCardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  lightningIconBox: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: '#EFF6FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #DBEAFE',
  },
  heroCardTitle: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#0F172A',
  },
  heroCardSub: {
    fontSize: '12.5px',
    color: '#64748B',
    marginTop: '2px',
    fontWeight: '600',
  },
  heroSetDueBtn: {
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    padding: '10px 18px',
    fontSize: '13px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  statCardGlass: {
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    borderRadius: '16px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statGlassHeader: {
    fontSize: '12px',
    color: '#E0E7FF',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
  },
  statGlassVal: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: '-0.01em',
  },
  statGlassSub: {
    fontSize: '11.5px',
    color: '#DBEAFE',
    fontWeight: '500',
  },
  contentArea: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderRadius: '14px',
    backgroundColor: '#F8FAFC',
    border: '1.5px solid #E2E8F0',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
    fontSize: '13.5px',
    color: '#0F172A',
  },
  clearSearchBtn: {
    background: 'none',
    border: 'none',
    color: '#94A3B8',
    cursor: 'pointer',
  },
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflowX: 'auto',
    paddingBottom: '2px',
  },
  partyPill: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  },
  partyPillActive: {
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    borderColor: '#2563EB',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
  },
  statusTabBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    borderBottom: '1.5px solid #F1F5F9',
    paddingBottom: '0',
    overflowX: 'auto',
  },
  statusTabBtn: {
    background: 'none',
    border: 'none',
    padding: '10px 4px 12px 4px',
    fontSize: '13.5px',
    fontWeight: '700',
    color: '#64748B',
    cursor: 'pointer',
    position: 'relative',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s ease',
  },
  statusTabBtnActive: {
    color: '#2563EB',
  },
  activeTabLine: {
    position: 'absolute',
    bottom: '-1.5px',
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: '#2563EB',
    borderRadius: '3px 3px 0 0',
  },
  emptyCardContainer: {
    padding: '40px 16px',
    display: 'flex',
    justifyContent: 'center',
  },
  emptyCardInner: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '24px',
    padding: '36px 24px',
    maxWidth: '460px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
    gap: '10px',
  },
  greenShieldOuterCircle: {
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    backgroundColor: '#DCFCE7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 8px #F0FDF4',
    marginBottom: '6px',
  },
  greenShieldInnerCircle: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    backgroundColor: '#16A34A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
  },
  greenStatusBadge: {
    backgroundColor: '#DCFCE7',
    color: '#16A34A',
    padding: '3px 12px',
    borderRadius: '12px',
    fontSize: '11.5px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
  },
  orangeCalendarOuterCircle: {
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    backgroundColor: '#FEF3C7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 8px #FFFBEB',
    marginBottom: '6px',
  },
  orangeCalendarInnerCircle: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    backgroundColor: '#D97706',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(217, 119, 6, 0.3)',
  },
  orangeStatusBadge: {
    backgroundColor: '#FEF3C7',
    color: '#D97706',
    padding: '3px 12px',
    borderRadius: '12px',
    fontSize: '11.5px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
  },
  blueCalendarOuterCircle: {
    width: '76px',
    height: '76px',
    borderRadius: '50%',
    backgroundColor: '#DBEAFE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 8px #EFF6FF',
    marginBottom: '6px',
  },
  blueCalendarInnerCircle: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    backgroundColor: '#2563EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
  },
  blueStatusBadge: {
    backgroundColor: '#DBEAFE',
    color: '#2563EB',
    padding: '3px 12px',
    borderRadius: '12px',
    fontSize: '11.5px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
  },
  emptyCardHeading: {
    fontSize: '17px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '4px 0 0 0',
  },
  emptyCardSub: {
    fontSize: '12.5px',
    color: '#64748B',
    margin: 0,
    lineHeight: '1.45',
  },
  greenViewBtn: {
    marginTop: '10px',
    backgroundColor: '#16A34A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    padding: '10px 22px',
    fontSize: '13px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
  },
  orangeViewBtn: {
    marginTop: '10px',
    backgroundColor: '#D97706',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    padding: '10px 22px',
    fontSize: '13px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(217, 119, 6, 0.3)',
  },
  blueViewBtn: {
    marginTop: '10px',
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    padding: '10px 22px',
    fontSize: '13px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
  },
  cardsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  custCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '18px',
    padding: '16px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  custAvatar: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: '#DBEAFE',
    color: '#2563EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: '800',
    flexShrink: 0,
  },
  custHeaderMeta: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  nameAndType: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  custNameText: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#0F172A',
  },
  typeBadge: {
    fontSize: '10.5px',
    padding: '2px 7px',
    borderRadius: '6px',
    backgroundColor: '#F1F5F9',
    color: '#475569',
    fontWeight: '700',
  },
  custMobileText: {
    fontSize: '12px',
    color: '#64748B',
    fontWeight: '600',
  },
  badgeLineWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '3px',
    flexWrap: 'wrap',
  },
  dueTodayYellowBadge: {
    backgroundColor: '#FEF3C7',
    color: '#B45309',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
  },
  overdueRedBadge: {
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
  },
  upcomingBlueBadge: {
    backgroundColor: '#EFF6FF',
    color: '#2563EB',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
  },
  autoReminderPillWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '4px',
  },
  autoReminderPurplePill: {
    backgroundColor: '#F5F3FF',
    border: '1px solid #DDD6FE',
    color: '#7C3AED',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },
  custDueDateLbl: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#1E293B',
    marginTop: '4px',
  },
  custBalanceBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  custBalanceVal: {
    fontSize: '17px',
    fontWeight: '800',
    color: '#DC2626',
    letterSpacing: '-0.01em',
  },
  custBalanceLbl: {
    fontSize: '11px',
    color: '#94A3B8',
    fontWeight: '600',
  },
  sentBadgeWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  sentGreenPill: {
    fontSize: '11px',
    color: '#059669',
    backgroundColor: '#ECFDF5',
    border: '1px solid #D1FAE5',
    padding: '2px 8px',
    borderRadius: '8px',
    fontWeight: '700',
  },
  actionsBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    borderTop: '1px solid #F1F5F9',
    paddingTop: '12px',
  },
  actionBtnWhatsApp: {
    backgroundColor: '#ECFDF5',
    color: '#059669',
    border: '1px solid #A7F3D0',
    borderRadius: '10px',
    padding: '8px 4px',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  actionBtnSMS: {
    backgroundColor: '#EFF6FF',
    color: '#2563EB',
    border: '1px solid #BFDBFE',
    borderRadius: '10px',
    padding: '8px 4px',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  actionBtnCall: {
    backgroundColor: '#F0FDFA',
    color: '#0D9488',
    border: '1px solid #99F6E4',
    borderRadius: '10px',
    padding: '8px 4px',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  actionBtnDate: {
    backgroundColor: '#F5F3FF',
    color: '#7C3AED',
    border: '1px solid #DDD6FE',
    borderRadius: '10px',
    padding: '8px 4px',
    fontSize: '12px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '12px',
  },
  loadingText: {
    fontSize: '13px',
    color: '#64748B',
    fontWeight: '600',
  },
};

export default RemindersHome;
